import React, { useState } from "react";
import { Building2, ChevronDown, Check, Plus, Layers } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useCompany } from "@/context/CompanyContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function CompanySwitcher() {
  const { companies, active, activeId, setActiveId, reload } = useCompany();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", work_email: "" });
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/companies", {
        name: form.name.trim(),
        work_email: form.work_email.trim() || undefined,
      });
      toast.success(`${data.name} created`);
      setForm({ name: "", work_email: "" });
      setOpen(false);
      await reload();
      setActiveId(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  const isAll = activeId === "__all__";
  const displayName = isAll
    ? "All companies"
    : active?.name || (companies.length === 0 ? "No company yet" : "Choose company");
  const DisplayIcon = isAll ? Layers : Building2;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl hover:bg-emerald-50 border border-emerald-100 bg-emerald-50/40 transition-colors text-sm"
            data-testid="company-switcher"
          >
            <DisplayIcon className="h-4 w-4 text-emerald-700" />
            <span className="font-semibold text-emerald-800 max-w-[150px] truncate">
              {displayName}
            </span>
            <ChevronDown className="h-3.5 w-3.5 text-emerald-700" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="bottom" className="w-64">
          <DropdownMenuLabel>Your companies</DropdownMenuLabel>
          {companies.length > 1 && (
            <DropdownMenuItem
              onClick={() => !isAll && setActiveId("__all__")}
              className="flex items-center justify-between gap-2 cursor-pointer"
              data-testid="company-see-all"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <Layers className="h-4 w-4 text-emerald-600 shrink-0" />
                <div className="text-sm font-semibold">See all companies</div>
              </div>
              {isAll && <Check className="h-4 w-4 text-emerald-600 shrink-0" />}
            </DropdownMenuItem>
          )}
          {companies.length === 0 && (
            <div className="px-2 py-3 text-xs text-slate-500">
              No companies yet. Create one to start logging work.
            </div>
          )}
          {companies.map((c) => (
            <DropdownMenuItem
              key={c.id}
              onClick={() => c.id !== activeId && setActiveId(c.id)}
              data-testid={`company-option-${c.id}`}
              className="flex items-center justify-between gap-2 cursor-pointer"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{c.name}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {c.work_email} · {c.role}
                </div>
              </div>
              {c.id === activeId && <Check className="h-4 w-4 text-emerald-600 shrink-0" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setOpen(true)} data-testid="company-create">
            <Plus className="h-4 w-4 mr-2 text-emerald-600" /> Create new company
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="company-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Create new company</DialogTitle>
            <DialogDescription>You'll be the admin. You can invite teammates after.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Company name</label>
              <input
                className="tg-input mt-1"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Acme Corp"
                data-testid="company-name-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Work email at this company (optional)</label>
              <input
                className="tg-input mt-1"
                value={form.work_email}
                onChange={(e) => setForm({ ...form, work_email: e.target.value })}
                placeholder="Leave blank to reuse your account email"
                data-testid="company-email-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setOpen(false)} className="tg-btn-secondary" data-testid="company-create-cancel">
              Cancel
            </button>
            <button
              onClick={create}
              disabled={busy || !form.name.trim()}
              className="tg-btn-primary disabled:opacity-50"
              data-testid="company-create-submit"
            >
              {busy ? "Creating…" : "Create"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
